class AdvancedMarqueeComponent extends HTMLElement {
  constructor() {
    super();

    this.loop = this.loop.bind(this);
    this.handleMouseEnter = this.handleMouseEnter.bind(this);
    this.handleMouseLeave = this.handleMouseLeave.bind(this);
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.handleWindowResize = this.handleWindowResize.bind(this);
    this.handleMotionPreferenceChange =
      this.handleMotionPreferenceChange.bind(this);

    this.wasRunning = false;
    this.resizeTimeout = null;
    this.componentWidth = 0;
    this.pendingWidth = null;
    this.pauseReasons = new Set();
    this.intersectionObserver = null;
    this.resizeObserver = null;
    this.motionQuery = null;
    this.usesWindowResize = false;
    this.isReducedMotion = false;
  }

  connectedCallback() {
    if (this.initialized) return;

    this.setupMotionPreference();
    requestAnimationFrame(() => this.init());
  }

  disconnectedCallback() {
    this.destroy(true);
  }

  setupMotionPreference() {
    if (this.motionQuery || typeof window === 'undefined' || !window.matchMedia)
      return;

    this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.isReducedMotion = this.motionQuery.matches;

    const addListener = this.motionQuery.addEventListener
      ? (query, handler) => query.addEventListener('change', handler)
      : (query, handler) => query.addListener(handler);

    addListener(this.motionQuery, this.handleMotionPreferenceChange);
  }

  teardownMotionPreference() {
    if (!this.motionQuery) return;

    const removeListener = this.motionQuery.removeEventListener
      ? (query, handler) => query.removeEventListener('change', handler)
      : (query, handler) => query.removeListener(handler);

    removeListener(this.motionQuery, this.handleMotionPreferenceChange);
    this.motionQuery = null;
  }

  handleMotionPreferenceChange(event) {
    this.isReducedMotion = Boolean(event?.matches);

    if (this.initialized) {
      this.restart();
    }
  }

  async init() {
    if (this.initialized) return;

    let content = this.querySelector('.marquee__content');

    if (!content) {
      return;
    }

    this.content = content;
    this.originalMarkup = this.originalMarkup ?? content.innerHTML;
    this.pauseReasons.clear();
    this.pendingWidth = null;

    await this.waitForImages(content);

    if (!this.isConnected) return;

    content = this.querySelector('.marquee__content');

    if (!content) {
      return;
    }

    this.content = content;

    if (this.initialized) return;

    if (!content.children.length) {
      return;
    }

    if (this.isReducedMotion) {
      content.style.transform = '';
      content.style.willChange = '';
      this.initialized = true;
      return;
    }

    const baseMarkup = this.originalMarkup;

    this.cycleWidth = content.scrollWidth;

    if (!this.cycleWidth) {
      return;
    }

    const containerWidth = this.getBoundingClientRect().width || 0;
    this.componentWidth = containerWidth;
    const targetWidth = Math.max(
      this.cycleWidth * 2,
      containerWidth + this.cycleWidth
    );

    const estimatedNeeded = Math.ceil(targetWidth / this.cycleWidth);
    const maxDuplications = Math.min(400, Math.max(estimatedNeeded + 2, 3));
    let totalWidth = this.cycleWidth;
    let duplicationCount = 1;

    while (totalWidth < targetWidth && duplicationCount < maxDuplications) {
      content.insertAdjacentHTML('beforeend', baseMarkup);
      duplicationCount += 1;
      totalWidth = content.scrollWidth;
    }

    this.direction =
      this.dataset.movementDirection === 'reverse' ? 'reverse' : 'normal';

    this.speedSetting = Number(this.dataset.speedFactor) || 50;
    this.speed = this.calculateSpeed(this.speedSetting);
    this.offset = this.direction === 'normal' ? 0 : -this.cycleWidth;
    this.lastTimestamp = undefined;

    content.style.willChange = 'transform';
    content.style.transform = `translate3d(${this.offset}px, 0, 0)`;

    this.addEventListener('mouseenter', this.handleMouseEnter);
    this.addEventListener('mouseleave', this.handleMouseLeave);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);

    this.setupIntersectionObserver();
    this.setupResizeHandling();

    this.startAnimation();
    this.initialized = true;
  }

  async waitForImages(root) {
    const images = Array.from(root.querySelectorAll('img'));

    if (!images.length) return;

    await Promise.all(
      images.map(
        (image) =>
          new Promise((resolve) => {
            if (image.complete) {
              resolve();
              return;
            }

            image.addEventListener('load', resolve, { once: true });
            image.addEventListener('error', resolve, { once: true });
          })
      )
    );
  }

  calculateSpeed(setting) {
    const clamped = Math.min(100, Math.max(10, setting));
    const normalized = (100 - clamped) / 90;
    const minSpeed = 40; // px/s
    const maxSpeed = 220; // px/s

    return minSpeed + normalized * (maxSpeed - minSpeed);
  }

  startAnimation() {
    if (
      this.rafId ||
      !this.content ||
      this.pauseReasons.size > 0 ||
      this.isReducedMotion
    ) {
      return;
    }

    this.lastTimestamp = undefined;
    this.rafId = requestAnimationFrame(this.loop);
  }

  stopAnimation() {
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  pause(reason = 'manual') {
    if (this.pauseReasons.has(reason)) return;
    this.pauseReasons.add(reason);
    this.stopAnimation();
  }

  resume(reason = 'manual') {
    if (!this.pauseReasons.has(reason)) return;
    this.pauseReasons.delete(reason);
    this.lastTimestamp = undefined;

    if (this.pauseReasons.size === 0) {
      this.startAnimation();
    }
  }

  loop(timestamp) {
    if (!this.content) return;

    if (this.pauseReasons.size > 0 || this.isReducedMotion) {
      this.stopAnimation();
      return;
    }

    if (this.lastTimestamp === undefined) {
      this.lastTimestamp = timestamp;
    }

    const delta = timestamp - this.lastTimestamp;
    this.lastTimestamp = timestamp;

    const distance = (this.speed * delta) / 1000;

    if (this.direction === 'normal') {
      this.offset -= distance;

      if (this.offset <= -this.cycleWidth) {
        this.offset += this.cycleWidth;
      }
    } else {
      this.offset += distance;

      if (this.offset >= 0) {
        this.offset -= this.cycleWidth;
      }
    }

    this.content.style.transform = `translate3d(${this.offset}px, 0, 0)`;

    this.rafId = requestAnimationFrame(this.loop);
  }

  handleMouseEnter() {
    this.pause('hover');
  }

  handleMouseLeave() {
    this.resume('hover');
  }

  handleVisibilityChange() {
    if (!this.initialized) return;

    if (document.hidden) {
      this.wasRunning = Boolean(this.rafId) && this.pauseReasons.size === 0;
      this.pause('visibility');
      return;
    }

    this.pauseReasons.delete('visibility');

    if (this.wasRunning && this.pauseReasons.size === 0) {
      this.startAnimation();
    }

    this.wasRunning = false;
  }

  setupIntersectionObserver() {
    if (typeof IntersectionObserver === 'undefined') {
      return;
    }

    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }

    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.target !== this) return;

          if (entry.isIntersecting) {
            this.resume('intersection');
          } else {
            this.pause('intersection');
          }
        });
      },
      {
        threshold: 0.05
      }
    );

    this.intersectionObserver.observe(this);
  }

  setupResizeHandling() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver((entries) => {
        const entry = entries && entries[0];
        const width = entry?.contentRect?.width;
        this.handleResize(typeof width === 'number' ? width : undefined);
      });

      this.resizeObserver.observe(this);
      this.usesWindowResize = false;
      return;
    }

    if (!this.usesWindowResize && typeof window !== 'undefined') {
      window.addEventListener('resize', this.handleWindowResize);
      this.usesWindowResize = true;
    }
  }

  handleWindowResize() {
    this.handleResize();
  }

  handleResize(forcedWidth) {
    if (!this.initialized) return;

    const width =
      typeof forcedWidth === 'number'
        ? forcedWidth
        : this.getBoundingClientRect().width || 0;

    if (Math.abs(width - (this.pendingWidth ?? this.componentWidth)) < 1) {
      return;
    }

    this.pendingWidth = width;

    clearTimeout(this.resizeTimeout);
    this.pause('resize');

    this.resizeTimeout = setTimeout(() => {
      this.componentWidth = this.pendingWidth ?? this.componentWidth;
      this.pendingWidth = null;
      this.pauseReasons.delete('resize');
      this.resizeTimeout = null;
      this.restart();
    }, 150);
  }

  restart() {
    this.destroy(false);
    this.init();
  }

  destroy(clearOriginalMarkup) {
    this.stopAnimation();

    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = null;
    this.pendingWidth = null;

    document.removeEventListener(
      'visibilitychange',
      this.handleVisibilityChange
    );
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
      this.intersectionObserver = null;
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    if (this.usesWindowResize && typeof window !== 'undefined') {
      window.removeEventListener('resize', this.handleWindowResize);
      this.usesWindowResize = false;
    }

    this.removeEventListener('mouseenter', this.handleMouseEnter);
    this.removeEventListener('mouseleave', this.handleMouseLeave);

    if (this.content && this.originalMarkup) {
      this.content.innerHTML = this.originalMarkup;
      this.content.style.transform = '';
      this.content.style.willChange = '';
    }

    if (clearOriginalMarkup) {
      this.originalMarkup = null;
      this.teardownMotionPreference();
      this.isReducedMotion = false;
    }

    this.pauseReasons.clear();
    this.content = null;
    this.cycleWidth = 0;
    this.initialized = false;
    this.wasRunning = false;
    this.componentWidth = 0;
  }
}

if (!customElements.get('advanced-marquee-component')) {
  customElements.define('advanced-marquee-component', AdvancedMarqueeComponent);
}
